/**
 * 全项目施工计划体检：定位没有计划或不能自动落实的具体条件，并给出业务上的修复步骤。
 * 体检 GET 只读；“立即扫描”只幂等生成内部计划草稿，不创建分包派工、不发送站内消息/短信/邮件。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, ClipboardCheck, Loader2, RefreshCw, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const PAGE_SIZE = 25;
const statusLabel = { ready: '计划已生成', review: '计划待复核', blocked: '存在待解决卡点' };

export default function ConstructionPlanReadiness({ open, onClose, onOpenPlan, onChanged }) {
  const { api } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('blocked');
  const [page, setPage] = useState(0);

  /** 打开面板先读真实项目状态；不会在 GET 中悄悄创建施工计划。 */
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/construction-plan/readiness');
      setData(response.data);
    } catch (e) {
      setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { if (open) load(); }, [open, load]);

  /** 显式触发全范围扫描；后端只新建缺失的施工计划草稿，保留所有原安排与人工派工门禁。 */
  const scan = async () => {
    if (scanning || loading) return;
    setScanning(true);
    setError('');
    try {
      const response = await api.post('/construction-plan/scan', {});
      setData(response.data);
      onChanged?.();
    } catch (e) {
      setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : e.message);
    } finally {
      setScanning(false);
    }
  };

  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    return (data?.projects || []).filter(project => {
      if (filter !== 'all' && project.status !== filter) return false;
      if (!needle) return true;
      return `${project.address} ${project.projectId} ${project.blockers.map(x => `${x.title} ${x.detail || ''} ${x.resolution}`).join(' ')} ${project.warnings.map(x => `${x.title} ${x.detail || ''}`).join(' ')}`.toLocaleLowerCase().includes(needle);
    });
  }, [data, filter, search]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  useEffect(() => { setPage(0); }, [filter, search]);

  return <Dialog open={open} onOpenChange={value => { if (!value && !scanning) onClose(); }}>
    <DialogContent className="sm:max-w-5xl max-h-[92dvh] overflow-y-auto bg-white p-4 sm:p-6" aria-describedby="construction-plan-readiness-description">
      <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-100 bg-white pb-3">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-brand-600" aria-hidden="true" />全项目施工计划体检</DialogTitle>
          <DialogDescription id="construction-plan-readiness-description">识别计划覆盖情况、自动匹配卡点和负责人可执行的处理办法。</DialogDescription>
        </DialogHeader>
        <Button variant="outline" className="min-h-11 shrink-0" disabled={loading || scanning} onClick={load}><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />刷新体检</Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
        <p className="min-w-0 text-sm text-slate-700">{data?.scope === 'company' ? '全公司项目' : '当前账号负责范围内的项目'}；扫描只生成内部计划草稿，不会派工或通知分包商。</p>
        <Button className="min-h-11" disabled={loading || scanning} onClick={scan}>{scanning ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="h-4 w-4" aria-hidden="true" />}{scanning ? '正在扫描' : '立即扫描并补齐计划'}</Button>
      </div>

      {data?.scan && <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">已扫描 {data.scan.checked} 个施工项目，新增 {data.scan.created} 份计划草稿；{data.scan.blocked} 个项目缺少生成条件，{data.scan.failed} 个项目生成失败。下方列出具体卡点。</p>}
      {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error} <button className="ml-2 min-h-11 underline" disabled={loading || scanning} onClick={load}>重试</button></p>}
      {loading && !data && <p role="status" className="flex items-center gap-2 py-6 text-sm text-slate-600"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />正在核对项目和施工记录…</p>}

      {data && <>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="计划体检统计">
          {[["项目总数", data.total], ["已生成计划", data.planGenerated], ["可继续复核", data.needsReview], ["存在卡点", data.blocked]].map(([label, count]) => <div key={label} className="rounded-xl border border-slate-200 bg-white p-3"><p className="text-xs text-slate-600">{label}</p><p className="mt-1 font-display text-xl font-bold text-slate-900">{count}</p></div>)}
        </div>

        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
          <label className="relative block"><Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-500" aria-hidden="true" /><span className="sr-only">搜索项目或卡点</span><input className="min-h-11 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400" value={search} onChange={e => setSearch(e.target.value)} placeholder="搜索项目地址、编号或卡点" /></label>
          <label className="flex items-center gap-2 text-sm text-slate-700"><span>显示</span><select className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400" value={filter} onChange={e => setFilter(e.target.value)}><option value="blocked">待解决卡点</option><option value="review">待人工复核</option><option value="ready">已就绪</option><option value="all">全部项目</option></select></label>
        </div>

        <div className="max-h-[48dvh] space-y-3 overflow-y-auto pr-1" aria-live="polite">
          {pageRows.map(project => <article key={project.projectId} className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0"><h3 className="break-words font-semibold text-slate-900">{project.address}</h3><p className="mt-1 text-xs text-slate-600">项目编号 {project.projectId} · 阶段 {project.stage || '未填写'}{project.projectStart ? ` · 开工日 ${project.projectStart}` : ' · 未登记开工日'}</p></div>
              <span className={`inline-flex min-h-8 items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${project.status === 'ready' ? 'bg-emerald-50 text-emerald-800' : project.status === 'review' ? 'bg-amber-50 text-amber-900' : 'bg-red-50 text-red-800'}`}>
                {project.status === 'ready' ? <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> : <AlertTriangle className="h-4 w-4" aria-hidden="true" />}{statusLabel[project.status] || '需要检查'}
              </span>
            </div>
            {project.blockers.length > 0 && <div className="mt-3 space-y-2">{project.blockers.map((item, index) => <div key={`${item.code}-${index}`} className="rounded-lg bg-amber-50 p-3 text-sm text-amber-950"><p className="font-semibold">{item.title}{item.detail ? ` · ${item.detail}` : ''}</p><p className="mt-1 leading-6">处理办法：{item.resolution}</p></div>)}</div>}
            {project.warnings.length > 0 && <details className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3"><summary className="min-h-11 cursor-pointer text-sm font-medium text-slate-800">复核建议：{project.warnings.length} 项</summary><div className="space-y-2">{project.warnings.map((item, index) => <p key={`${item.code}-${index}`} className="text-sm text-slate-700"><b>{item.title}{item.detail ? ` · ${item.detail}` : ''}：</b>{item.resolution}</p>)}</div></details>}
            <div className="mt-3 flex flex-wrap gap-2">
              {project.stage === 'construction' ? <Button variant="outline" className="min-h-11" onClick={() => onOpenPlan(project.projectId)}>{project.planExists ? '打开全流程计划' : '查看计划条件'}</Button> : <Button asChild variant="outline" className="min-h-11"><Link to={`/project/${encodeURIComponent(project.projectId)}`}>打开项目处理交接</Link></Button>}
            </div>
          </article>)}
          {!pageRows.length && <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-600">{filter === 'blocked' ? '没有待解决卡点。可切换筛选查看已就绪或待复核项目。' : '没有符合当前筛选的项目。'}</div>}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3 text-sm text-slate-600">
          <span>显示 {filtered.length ? page * PAGE_SIZE + 1 : 0}–{Math.min(filtered.length, (page + 1) * PAGE_SIZE)} / {filtered.length}</span>
          <div className="flex items-center gap-2"><Button variant="outline" size="sm" className="min-h-11" disabled={page <= 0} onClick={() => setPage(value => Math.max(0, value - 1))}><ArrowLeft className="h-4 w-4" aria-hidden="true" />上一页</Button><Button variant="outline" size="sm" className="min-h-11" disabled={page + 1 >= pageCount} onClick={() => setPage(value => Math.min(pageCount - 1, value + 1))}>下一页<ArrowRight className="h-4 w-4" aria-hidden="true" /></Button></div>
        </div>
      </>}
    </DialogContent>
  </Dialog>;
}
